<template>
    <BaseDialog v-model:open="open" :title="t('profile.collaboration.sources.title')">
        <p class="body text-ink-muted">{{ t("profile.collaboration.sources.description") }}</p>
        <FreeTextUpload v-model:free-text="freeText" :free-text-enabled="freeTextEnabled" />

        <template v-if="documentUploadEnabled">
            <ProfileDocumentsHeading />
            <div v-if="isFetchingLinkedSources && linkedSources == null" class="flex flex-col gap-2">
                <SkeletonLoader v-for="n in 2" :key="n" class="h-10 w-full rounded-lg" :aria-label="t('profile.collaboration.sources.loading')" />
            </div>
            <div v-else class="flex flex-col gap-2 max-h-64 overflow-y-auto">
                <div v-if="perspectiveTitle" class="flex items-center justify-between rounded-lg border border-border px-3 py-2 shrink-0">
                    <div class="flex items-center gap-2 min-w-0">
                        <ArchetypeDot :letters="perspectiveLetters" class="size-3" />
                        <div class="flex flex-wrap gap-x-4 items-baseline">
                            <span class="body-small truncate">{{ perspectiveTitle }}</span>
                            <span class="details text-ink-muted">{{ t("profile.collaboration.sources.fromPerspective") }}</span>
                        </div>
                    </div>
                </div>
                <div v-for="row in sourcedFiles" :key="row.id" class="flex items-center justify-between rounded-lg border border-border px-3 py-2 shrink-0">
                    <div class="flex items-center gap-2 min-w-0">
                        <i class="bi bi-file-text shrink-0" aria-hidden="true"></i>
                        <div class="flex flex-wrap gap-x-4 items-baseline">
                            <span class="body-small truncate">{{ row.name }}</span>
                            <span class="details text-ink-muted">
                                <I18nT v-if="row.last_used_at" keypath="profile.collaboration.sources.lastUsed">
                                    <template #date><BaseDate :value="row.last_used_at" format="datetime" /></template>
                                </I18nT>
                                <template v-else>{{ t("profile.collaboration.sources.notUsedYet") }}</template>
                            </span>
                        </div>
                    </div>
                    <BaseButton
                        theme="tertiary"
                        size="sm"
                        icon-only
                        :aria-label="t('profile.collaboration.sources.remove', { name: row.name })"
                        class="shrink-0"
                        :disabled="isDeletingSource"
                        @click="requestDelete(row.id)">
                        <i class="bi bi-x-lg" aria-hidden="true"></i>
                    </BaseButton>
                </div>
                <div v-if="sourcedFiles.length === 0 && !hasPerspectiveReport" class="text-center py-4">
                    <p class="body-small text-ink-muted">{{ t("profile.collaboration.sources.empty") }}</p>
                </div>
            </div>
            <BaseNotification v-if="deleteSourceError" theme="error" hide-dismiss :text="String(deleteSourceError)" />

            <ProfileFileUploadArea :accepted-file-types="acceptedFileTypes" :is-uploading="isUploading" @file-upload="(files) => handleFileUpload(files, topicName)" />
            <BaseNotification v-if="fileUploadError" theme="error" hide-dismiss :text="fileUploadError" />
        </template>

        <BaseCheckbox v-model="isConfirmChecked" shape="square">
            <p class="body text-ink">{{ t("profile.collaboration.sources.regenerateConfirm") }}</p>
        </BaseCheckbox>

        <BaseNotification v-if="createSourceError" theme="error" hide-dismiss :text="String(createSourceError)" />
        <template #footer>
            <BaseButton theme="tertiary" :disabled="isUploading" @click="open = false">{{ t("common.cancel") }}</BaseButton>
            <BaseTooltip :disabled="canRegenerate" side="top">
                <template #trigger>
                    <BaseButton theme="primary" :disabled="isUploading || !canRegenerate" data-testid="regenerate-button" @click="handleRegenerate">{{
                        t("profile.collaboration.sources.regenerate")
                    }}</BaseButton>
                </template>
                <template #content>
                    <span class="body-small">{{ t("profile.collaboration.sources.regenerateUnavailable") }}</span>
                </template>
            </BaseTooltip>
        </template>
    </BaseDialog>
    <ConfirmationDialog
        v-model:open="isConfirmDeleteDialogOpen"
        :title="t('profile.collaboration.sources.removeDialog.title')"
        :text="t('profile.collaboration.sources.removeDialog.text')"
        :confirm-label="t('profile.collaboration.sources.removeDialog.confirm')"
        @confirm="confirmDelete" />
</template>

<script setup lang="ts">
import type { OrganizationDetails } from "~vue/appTypes.ts";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { useProfileSources } from "~vue/composables/useProfileSources";
import { i18n } from "~vue/i18n";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import { getPerspectiveProfileTitle } from "~vue/utils/perspectiveUtils";
import { computed, type ComputedRef, inject, ref, watch } from "vue";
import { I18nT } from "vue-i18n";

import FreeTextUpload from "./FreeTextUpload.vue";
import ProfileDocumentsHeading from "./ProfileDocumentsHeading.vue";
import ProfileFileUploadArea from "./ProfileFileUploadArea.vue";

const { t } = i18n.global;

type ManageSourcesDialogProps = {
    topicName?: string;
    perspectiveLetters?: string | null;
    freeTextEnabled?: boolean;
};

const organizationDetails = inject<ComputedRef<OrganizationDetails | null>>("organization_details");

const { topicName, perspectiveLetters = null, freeTextEnabled = false } = defineProps<ManageSourcesDialogProps>();

const emit = defineEmits<{ regenerate: [freeText: string] }>();

const open = defineModel<boolean>("open", { default: false });

const isConfirmChecked = ref<boolean>(false);
const documentUploadEnabled = computed(() => organizationDetails?.value?.is_document_upload_enabled);

watch(open, (val) => {
    if (!val) {
        isConfirmChecked.value = false;
    }
});

const {
    uploadedFiles,
    acceptedFileTypes,
    handleFileUpload,
    isUploading,
    fileUploadError,
    linkedSources,
    isFetchingLinkedSources,
    createSourceError,
    isDeletingSource,
    handleDeleteSource,
    deleteSourceError,
} = useProfileSources();

const sourcedFiles = computed(() => linkedSources.value?.sources.filter((s) => s.topic === topicName) ?? []);

const hasPerspectiveReport = computed(() => Boolean(perspectiveLetters));

const perspectiveTitle = computed(() => getPerspectiveProfileTitle(perspectiveLetters));

const canRegenerate = computed(
    () => isConfirmChecked.value && (!!freeText.value.trim() || sourcedFiles.value.length > 0 || uploadedFiles.value.length > 0 || hasPerspectiveReport.value),
);

const freeText = ref<string>("");

const handleRegenerate = () => {
    open.value = false;
    isConfirmChecked.value = false;
    emit("regenerate", freeText.value.trim());
};

const isConfirmDeleteDialogOpen = ref<boolean>(false);
const pendingDeleteSourceId = ref<string | null>(null);

const requestDelete = (sourceId: string) => {
    pendingDeleteSourceId.value = sourceId;
    isConfirmDeleteDialogOpen.value = true;
};

const confirmDelete = async () => {
    const sourceId = pendingDeleteSourceId.value;
    pendingDeleteSourceId.value = null;
    if (!sourceId) {
        return;
    }
    try {
        await handleDeleteSource(sourceId);
    } catch {
        if (!deleteSourceError.value) {
            deleteSourceError.value = t("profile.collaboration.sources.deleteFailed");
        }
    }
};
</script>
